import { useState, useEffect, useCallback } from 'react';

interface LanguageInfo {
  code: string;
  name: string;
  script: string;
  system: string;
  route: string;
  status: string;
  rule_count?: number;
  case_sensitive?: boolean;
}

interface RuleEntry {
  pattern: string;
  replacement: string;
}

interface TranscriptionSystem {
  id: string;
  name: string;
  source: string;
  rule_count: number;
  editable: boolean;
  created_at?: string;
}

type Tab = 'overview' | 'test' | 'rules';

const SCRIPT_COLORS: Record<string, string> = {
  Cyrillic: 'bg-blue-900/30 text-blue-300',
  'Cyrillic/Latin': 'bg-blue-900/30 text-blue-300',
  'Glagolitic/Cyrillic': 'bg-blue-900/30 text-blue-300',
  Greek: 'bg-violet-900/30 text-violet-300',
  Armenian: 'bg-rose-900/30 text-rose-300',
  'Georgian (Mkhedruli)': 'bg-amber-900/30 text-amber-300',
  Hangul: 'bg-teal-900/30 text-teal-300',
  Arabic: 'bg-orange-900/30 text-orange-300',
  'Arabic (Sorani)': 'bg-orange-900/30 text-orange-300',
  'Arabic (Uyghur)': 'bg-orange-900/30 text-orange-300',
  'Arabic (Persian)': 'bg-orange-900/30 text-orange-300',
  'Arabic (Pashto)': 'bg-orange-900/30 text-orange-300',
  'Arabic (Urdu)': 'bg-orange-900/30 text-orange-300',
  Hebrew: 'bg-indigo-900/30 text-indigo-300',
  Latin: 'bg-zinc-700/30 text-zinc-300',
  'CJK (Simplified)': 'bg-red-900/30 text-red-300',
  'CJK (Traditional)': 'bg-red-900/30 text-red-300',
  'CJK + Kana': 'bg-red-900/30 text-red-300',
  'CJK + Latin': 'bg-red-900/30 text-red-300',
  CJK: 'bg-red-900/30 text-red-300',
  Thai: 'bg-emerald-900/30 text-emerald-300',
  Khmer: 'bg-emerald-900/30 text-emerald-300',
  Lao: 'bg-emerald-900/30 text-emerald-300',
  Myanmar: 'bg-emerald-900/30 text-emerald-300',
  Bengali: 'bg-pink-900/30 text-pink-300',
  Tamil: 'bg-pink-900/30 text-pink-300',
  Devanagari: 'bg-pink-900/30 text-pink-300',
  Kannada: 'bg-pink-900/30 text-pink-300',
  Malayalam: 'bg-pink-900/30 text-pink-300',
  Gujarati: 'bg-pink-900/30 text-pink-300',
  Gurmukhi: 'bg-pink-900/30 text-pink-300',
  Odia: 'bg-pink-900/30 text-pink-300',
  Sinhala: 'bg-pink-900/30 text-pink-300',
  Telugu: 'bg-pink-900/30 text-pink-300',
  Tibetan: 'bg-amber-900/30 text-amber-300',
  Syriac: 'bg-indigo-900/30 text-indigo-300',
  'Ethiopic (Geʿez)': 'bg-yellow-900/30 text-yellow-300',
  Cherokee: 'bg-cyan-900/30 text-cyan-300',
  'Canadian Syllabics': 'bg-cyan-900/30 text-cyan-300',
  "N'Ko": 'bg-yellow-900/30 text-yellow-300',
  Tifinagh: 'bg-yellow-900/30 text-yellow-300',
  Cuneiform: 'bg-stone-700/30 text-stone-300',
  Hieroglyphic: 'bg-stone-700/30 text-stone-300',
  Avestan: 'bg-stone-700/30 text-stone-300',
  Coptic: 'bg-indigo-900/30 text-indigo-300',
  Tangut: 'bg-stone-700/30 text-stone-300',
  Yi: 'bg-red-900/30 text-red-300',
  Vai: 'bg-yellow-900/30 text-yellow-300',
  Javanese: 'bg-emerald-900/30 text-emerald-300',
  Balinese: 'bg-emerald-900/30 text-emerald-300',
  Sundanese: 'bg-emerald-900/30 text-emerald-300',
  Manchu: 'bg-red-900/30 text-red-300',
  Shan: 'bg-emerald-900/30 text-emerald-300',
  'New Tai Lue': 'bg-emerald-900/30 text-emerald-300',
};

const ARM_SAMPLE = String.fromCodePoint(
  0x532, 0x561, 0x580, 0x587, 0x2C, 0x20,
  0x56B, 0x576, 0x579, 0x57A, 0x565, 0x57D, 0x20,
  0x565, 0x57D, 0x3F
);

const SAMPLE_TEXTS: Record<string, string> = {
  ell: 'Σήμερα είναι μια ωραία μέρα.',
  rus: 'Привет, как дела? Хорошо, спасибо.',
  ukr: 'Привіт, як справи? Добре, дякую.',
  bul: 'Здравейте, как сте? Добре съм.',
  srp: 'Здраво, како сте? Добро сам.',
  mkd: 'Здраво, како сте? Добро сум.',
  rue: 'Здраво, як ся маєте?',
  tat: 'Исәнмесез, хәлләрегез ничек?',
  kaz: 'Сәлеметсіз бе? Жақсы рахмет.',
  hye: ARM_SAMPLE,
  kat: 'გამარჯობა, როგორ ხარ?',
  kor: '안녕하세요, 어떻게 지내세요?',
  ckb: 'سڵاو، چۆنیت؟ باشم سوپاس.',
  uig: 'ياخشىمۇسىز؟ ياخشى رەھمەت.',
  heb: 'שָׁלוֹם, מָה שְׁלוֹמְךָ?',
};

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com';

const SOURCE_BADGE: Record<string, string> = {
  file: 'bg-teal-900/30 text-teal-300',
  inline: 'bg-violet-900/30 text-violet-300',
  database: 'bg-blue-900/30 text-blue-300',
};

// ── Overview tab ─────────────────────────────────────────────────────

function LangTable({ items, showRules }: { items: LanguageInfo[]; showRules: boolean }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse min-w-[600px]">
        <thead>
          <tr className="border-b border-zinc-700">
            <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Code</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Language</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Script</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">System</th>
            {showRules && <th className="px-3 py-2 text-right text-xs font-medium text-zinc-500">Rules</th>}
            {showRules && <th className="px-3 py-2 text-center text-xs font-medium text-zinc-500">Case</th>}
          </tr>
        </thead>
        <tbody>
          {items.map(lang => (
            <tr key={lang.code} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
              <td className="px-3 py-2 font-mono text-xs text-teal-400">{lang.code}</td>
              <td className="px-3 py-2 text-zinc-200">{lang.name}</td>
              <td className="px-3 py-2">
                <span className={`px-2 py-0.5 rounded text-xs ${SCRIPT_COLORS[lang.script] ?? 'bg-zinc-800 text-zinc-300'}`}>
                  {lang.script}
                </span>
              </td>
              <td className="px-3 py-2 text-zinc-400 text-xs">{lang.system}</td>
              {showRules && <td className="px-3 py-2 text-right font-mono text-xs text-zinc-300">{(lang.rule_count ?? 0).toLocaleString()}</td>}
              {showRules && (
                <td className="px-3 py-2 text-center">
                  {lang.case_sensitive
                    ? <span className="text-emerald-400 text-xs">Aa</span>
                    : <span className="text-zinc-600 text-xs">aa</span>
                  }
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WikiLuaTable({ items }: { items: WikiLuaInfo[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse min-w-[600px]">
        <thead>
          <tr className="border-b border-zinc-700">
            <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Code</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Language</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Wikt</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Available Modules</th>
          </tr>
        </thead>
        <tbody>
          {items.map(m => (
            <tr key={m.code} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
              <td className="px-3 py-2 font-mono text-xs text-teal-400">{m.code}</td>
              <td className="px-3 py-2 text-zinc-200">{m.name}</td>
              <td className="px-3 py-2 font-mono text-xs text-zinc-500">{m.wikt_code}</td>
              <td className="px-3 py-2 flex gap-1.5">
                {m.types.map(t => (
                  <span key={t} className={`px-2 py-0.5 rounded text-xs ${
                    t === 'ipa' ? 'bg-violet-900/30 text-violet-300' : 'bg-cyan-900/30 text-cyan-300'
                  }`}>
                    {t === 'ipa' ? 'Wiki Lua IPA' : 'Wiki Lua romanization'}
                  </span>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Overview({ languages, claudeApi, planned, wikiLua, wikiLuaScripts, wikiLuaTotal }: {
  languages: LanguageInfo[];
  claudeApi: LanguageInfo[];
  planned: LanguageInfo[];
  wikiLua: WikiLuaInfo[];
  wikiLuaScripts: WikiLuaInfo[];
  wikiLuaTotal: WikiLuaTotal | null;
}) {
  const totalRules = languages.reduce((s, l) => s + (l.rule_count ?? 0), 0);
  const luaIpaCount = wikiLua.filter(m => m.types.includes('ipa')).length;
  const luaRomCount = wikiLua.filter(m => m.types.includes('romanization')).length;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-zinc-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-teal-400">{languages.length}</div>
          <div className="text-xs text-zinc-500 mt-1">Metagraph (live)</div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-400">{claudeApi.length}</div>
          <div className="text-xs text-zinc-500 mt-1">Claude API</div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-amber-400">{planned.length}</div>
          <div className="text-xs text-zinc-500 mt-1">Planned</div>
        </div>
        <div className="bg-zinc-800 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-violet-400">{wikiLua.length}</div>
          <div className="text-xs text-zinc-500 mt-1">Wiki Lua</div>
        </div>
      </div>

      <div>
        <h2 className="text-zinc-100 font-medium mb-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-teal-400" />
          Metagraph — Rule-based ({languages.length})
        </h2>
        <p className="text-zinc-500 text-xs mb-3">Deterministic regex rules. Runs in Lambda, no API cost.</p>
        <LangTable items={languages} showRules />
      </div>

      <div>
        <h2 className="text-zinc-100 font-medium mb-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-blue-400" />
          Claude API — AI-generated ({claudeApi.length})
        </h2>
        <p className="text-zinc-500 text-xs mb-3">Complex scripts requiring context-aware transcription. Runs via Claude API calls.</p>
        <LangTable items={claudeApi} showRules={false} />
      </div>

      <div>
        <h2 className="text-zinc-100 font-medium mb-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-amber-400" />
          Planned Metagraph expansion ({planned.length})
        </h2>
        <p className="text-zinc-500 text-xs mb-3">Cyrillic, Caucasian, and other rule-based scripts to build transcription rules for.</p>
        <LangTable items={planned} showRules={false} />
      </div>

      <div>
        <h2 className="text-zinc-100 font-medium mb-2 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-violet-400" />
          Wiki Lua — Wiktionary modules ({wikiLua.length} languages)
        </h2>
        <p className="text-zinc-500 text-xs mb-3">
          IPA pronunciation and romanization Lua modules archived from Wiktionary. Source code for generating transcriptions — not data files.
          {wikiLuaTotal && <> {wikiLuaTotal.ipa} IPA + {wikiLuaTotal.romanization} romanization modules cataloged.</>}
        </p>
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="bg-violet-900/20 border border-violet-800/30 rounded-lg px-4 py-2 text-center">
            <div className="text-lg font-bold text-violet-300">{luaIpaCount}</div>
            <div className="text-xs text-zinc-500">Wiki Lua IPA</div>
          </div>
          <div className="bg-cyan-900/20 border border-cyan-800/30 rounded-lg px-4 py-2 text-center">
            <div className="text-lg font-bold text-cyan-300">{luaRomCount}</div>
            <div className="text-xs text-zinc-500">Wiki Lua romanization</div>
          </div>
          <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-center">
            <div className="text-lg font-bold text-zinc-300">{wikiLuaScripts.length}</div>
            <div className="text-xs text-zinc-500">Script-level translit</div>
          </div>
        </div>
        <WikiLuaTable items={wikiLua} />
        {wikiLuaScripts.length > 0 && (
          <div className="mt-4">
            <h3 className="text-zinc-400 text-sm font-medium mb-2">Script-level transliteration modules ({wikiLuaScripts.length})</h3>
            <p className="text-zinc-600 text-xs mb-2">Apply to all languages using a given writing system.</p>
            <WikiLuaTable items={wikiLuaScripts} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Test tab ─────────────────────────────────────────────────────────

function TestPanel({ languages, claudeApi }: { languages: LanguageInfo[]; claudeApi: LanguageInfo[] }) {
  const sorted = [...languages].sort((a, b) => a.code.localeCompare(b.code));
  const sortedClaude = [...claudeApi].sort((a, b) => a.code.localeCompare(b.code));
  const allTestable = [...sorted, ...sortedClaude];
  const [selectedLang, setSelectedLang] = useState(sorted[0]?.code ?? '');
  const [systems, setSystems] = useState<TranscriptionSystem[]>([]);
  const [selectedSystem, setSelectedSystem] = useState('');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batchResults, setBatchResults] = useState<Array<{ code: string; name: string; input: string; output: string | null; error?: string }>>([]);
  const [batchLoading, setBatchLoading] = useState(false);

  useEffect(() => {
    if (!selectedLang) return;
    setSystems([]);
    setSelectedSystem('');
    fetch(`${BASE_URL}/api/admin/metagraph/rules?language=${selectedLang}`)
      .then(r => r.json() as Promise<{ systems: TranscriptionSystem[] }>)
      .then(d => {
        const s = d.systems ?? [];
        setSystems(s);
        if (s.length > 0) setSelectedSystem(s[0]!.id);
      })
      .catch(() => {});

    if (SAMPLE_TEXTS[selectedLang]) {
      setInput(SAMPLE_TEXTS[selectedLang]!);
    } else {
      setInput('');
    }
    setOutput('');
    setError(null);
  }, [selectedLang]);

  const runTest = useCallback(async () => {
    if (!input.trim() || !selectedLang) return;
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, string> = { text: input, language: selectedLang };
      if (selectedSystem) body.system = selectedSystem;
      const res = await fetch(`${BASE_URL}/api/admin/metagraph/romanize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json() as { error?: string };
        throw new Error(err.error || `${res.status}`);
      }
      const data = await res.json() as { output: string };
      setOutput(data.output);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    } finally {
      setLoading(false);
    }
  }, [input, selectedLang, selectedSystem]);

  const runAllSamples = useCallback(async () => {
    setBatchLoading(true);
    setBatchResults([]);
    const items = allTestable
      .filter(l => SAMPLE_TEXTS[l.code])
      .map(l => ({ text: SAMPLE_TEXTS[l.code]!, language: l.code }));
    try {
      const res = await fetch(`${BASE_URL}/api/admin/metagraph/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json() as { results: Array<{ input: string; output: string | null; language: string; error?: string }> };
      setBatchResults(data.results.map(r => ({
        code: r.language,
        name: allTestable.find(l => l.code === r.language)?.name ?? r.language,
        input: r.input,
        output: r.output,
        error: r.error,
      })));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Batch test failed');
    } finally {
      setBatchLoading(false);
    }
  }, [allTestable]);

  const selectedMeta = allTestable.find(l => l.code === selectedLang);

  return (
    <div className="space-y-6">
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-zinc-500 text-xs block mb-1">Language</label>
            <select
              value={selectedLang}
              onChange={e => setSelectedLang(e.target.value)}
              className="w-full bg-zinc-900 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm"
            >
              <optgroup label={`Metagraph (${sorted.length})`}>
                {sorted.map(l => (
                  <option key={l.code} value={l.code}>{l.code} — {l.name}</option>
                ))}
              </optgroup>
              <optgroup label={`Claude API (${sortedClaude.length})`}>
                {sortedClaude.map(l => (
                  <option key={l.code} value={l.code}>{l.code} — {l.name}</option>
                ))}
              </optgroup>
            </select>
          </div>
          {systems.length > 0 && (
            <div className="flex-1 min-w-[200px]">
              <label className="text-zinc-500 text-xs block mb-1">Transcription system</label>
              <select
                value={selectedSystem}
                onChange={e => setSelectedSystem(e.target.value)}
                className="w-full bg-zinc-900 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm"
              >
                {systems.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.rule_count} rules, {s.source})
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={runTest}
            disabled={loading || !input.trim()}
            className="px-4 py-1.5 rounded text-sm font-medium bg-teal-800 text-teal-200 hover:bg-teal-700 disabled:opacity-50"
          >
            {loading ? 'Running...' : 'Transcribe'}
          </button>
          <button
            onClick={runAllSamples}
            disabled={batchLoading}
            className="px-4 py-1.5 rounded text-sm font-medium bg-zinc-700 text-zinc-200 hover:bg-zinc-600 disabled:opacity-50"
          >
            {batchLoading ? 'Testing...' : 'Test All Samples'}
          </button>
        </div>

        {selectedMeta && (
          <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
            <span>Script: <span className="text-zinc-300">{selectedMeta.script}</span></span>
            <span>System: <span className="text-zinc-300">{selectedMeta.system}</span></span>
            {selectedMeta.rule_count !== undefined && <span>Built-in rules: <span className="text-zinc-300">{selectedMeta.rule_count.toLocaleString()}</span></span>}
            {selectedMeta.case_sensitive !== undefined && <span>Case: <span className="text-zinc-300">{selectedMeta.case_sensitive ? 'sensitive' : 'lowercase'}</span></span>}
            {systems.length > 0 && <span>Systems: <span className="text-zinc-300">{systems.length}</span></span>}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-zinc-500 text-xs block mb-1">Input ({selectedMeta?.script ?? 'native script'})</label>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Enter text in native script..."
              className="w-full bg-zinc-900 text-zinc-200 rounded border border-zinc-700 px-3 py-2 text-sm font-mono min-h-[120px] resize-y focus:border-teal-600 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-zinc-500 text-xs block mb-1">Output (transcribed)</label>
            <div className="w-full bg-zinc-900 rounded border border-zinc-700 px-3 py-2 text-sm font-mono min-h-[120px] text-zinc-200">
              {output || <span className="text-zinc-600 italic">Click Transcribe to see output</span>}
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-800/50 rounded px-3 py-2 text-red-300 text-xs">{error}</div>
        )}
      </div>

      {batchResults.length > 0 && (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-700">
            <h3 className="text-zinc-100 font-medium text-sm">Batch Test Results — {batchResults.length} languages</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse min-w-[700px]">
              <thead>
                <tr className="border-b border-zinc-700">
                  <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Language</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Input</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Output</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-zinc-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {batchResults.map(r => (
                  <tr key={r.code} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="px-3 py-2">
                      <span className="font-mono text-xs text-teal-400">{r.code}</span>
                      <span className="text-zinc-400 text-xs ml-2">{r.name}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-300 max-w-[200px] truncate">{r.input}</td>
                    <td className="px-3 py-2 font-mono text-xs text-zinc-200 max-w-[200px] truncate">{r.output ?? '—'}</td>
                    <td className="px-3 py-2 text-center">
                      {r.error ? (
                        <span className="px-2 py-0.5 rounded text-xs bg-red-900 text-red-300">{r.error}</span>
                      ) : r.output ? (
                        <span className="text-emerald-400">&#10003;</span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rules tab ────────────────────────────────────────────────────────

function RulesViewer({ allLanguages }: { allLanguages: LanguageInfo[] }) {
  const sorted = [...allLanguages].sort((a, b) => a.code.localeCompare(b.code));
  const [selectedLang, setSelectedLang] = useState(sorted[0]?.code ?? '');
  const [systems, setSystems] = useState<TranscriptionSystem[]>([]);
  const [loadingSystems, setLoadingSystems] = useState(false);
  const [systemsError, setSystemsError] = useState<string | null>(null);

  // Drill-down into a specific system
  const [activeSystem, setActiveSystem] = useState<TranscriptionSystem | null>(null);
  const [rules, setRules] = useState<RuleEntry[]>([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [filter, setFilter] = useState('');
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // New system creation
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');

  const fetchSystems = useCallback(async (lang: string) => {
    setLoadingSystems(true);
    setSystemsError(null);
    setSystems([]);
    setActiveSystem(null);
    setShowNewForm(false);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/metagraph/rules?language=${lang}`);
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json() as { systems: TranscriptionSystem[] };
      setSystems(data.systems ?? []);
    } catch (err) {
      setSystemsError(err instanceof Error ? err.message : 'Failed to load systems');
    } finally {
      setLoadingSystems(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLang) fetchSystems(selectedLang);
  }, [selectedLang, fetchSystems]);

  const openSystem = useCallback(async (sys: TranscriptionSystem) => {
    setActiveSystem(sys);
    setEditing(false);
    setSaveMsg(null);
    setFilter('');
    setLoadingRules(true);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/metagraph/rules?language=${selectedLang}&system=${sys.id}`);
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json() as { rules: RuleEntry[] };
      setRules(data.rules ?? []);
    } catch {
      setRules([]);
    } finally {
      setLoadingRules(false);
    }
  }, [selectedLang]);

  const startEdit = useCallback(() => {
    setEditText(rules.map(r => `"${r.pattern}","${r.replacement}"`).join('\n'));
    setEditing(true);
    setSaveMsg(null);
  }, [rules]);

  const saveRules = useCallback(async () => {
    if (!activeSystem) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const parsed: RuleEntry[] = [];
      for (const line of editText.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        const match = trimmed.match(/^"(.+?)","(.*)"$/);
        if (!match) throw new Error(`Invalid rule format: ${trimmed}`);
        parsed.push({ pattern: match[1]!, replacement: match[2]! });
      }

      const res = await fetch(`${BASE_URL}/api/admin/metagraph/rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language: selectedLang,
          system_id: activeSystem.id,
          system_name: activeSystem.name,
          rules: parsed,
        }),
      });
      if (!res.ok) {
        const err = await res.json() as { error?: string };
        throw new Error(err.error || `${res.status}`);
      }
      const data = await res.json() as { count: number };
      setSaveMsg(`Saved ${data.count} rules`);
      setEditing(false);
      setRules(parsed);
      setActiveSystem({ ...activeSystem, rule_count: parsed.length });
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [editText, selectedLang, activeSystem]);

  const createSystem = useCallback(async () => {
    if (!newName.trim()) return;
    const slug = newName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (!slug) return;

    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch(`${BASE_URL}/api/admin/metagraph/rules`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          language: selectedLang,
          system_id: slug,
          system_name: newName.trim(),
          rules: [],
        }),
      });
      if (!res.ok) {
        const err = await res.json() as { error?: string };
        throw new Error(err.error || `${res.status}`);
      }
      setShowNewForm(false);
      setNewName('');
      await fetchSystems(selectedLang);
      const newSys: TranscriptionSystem = { id: slug, name: newName.trim(), source: 'database', rule_count: 0, editable: true };
      openSystem(newSys);
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  }, [newName, selectedLang, fetchSystems, openSystem]);

  const deleteSystem = useCallback(async (sys: TranscriptionSystem) => {
    if (!confirm(`Delete "${sys.name}" and all its rules?`)) return;
    try {
      const res = await fetch(`${BASE_URL}/api/admin/metagraph/rules?language=${selectedLang}&system=${sys.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setActiveSystem(null);
      fetchSystems(selectedLang);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }, [selectedLang, fetchSystems]);

  const filtered = filter
    ? rules.filter(r => r.pattern.includes(filter) || r.replacement.includes(filter))
    : rules;

  const selectedMeta = sorted.find(l => l.code === selectedLang);

  return (
    <div className="space-y-4">
      {/* Language selector */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-zinc-500 text-xs block mb-1">Language</label>
          <select
            value={selectedLang}
            onChange={e => setSelectedLang(e.target.value)}
            className="w-full bg-zinc-800 text-zinc-200 rounded px-3 py-1.5 border border-zinc-700 text-sm"
          >
            {sorted.map(l => (
              <option key={l.code} value={l.code}>
                {l.code} — {l.name}
              </option>
            ))}
          </select>
        </div>
        {selectedMeta && (
          <div className="flex flex-wrap gap-4 text-xs text-zinc-500 py-1.5">
            <span>Script: <span className="text-zinc-300">{selectedMeta.script}</span></span>
            <span>Default system: <span className="text-zinc-300">{selectedMeta.system}</span></span>
          </div>
        )}
      </div>

      {systemsError && (
        <div className="bg-red-900/30 border border-red-800/50 rounded px-3 py-2 text-red-300 text-xs">{systemsError}</div>
      )}

      {loadingSystems ? (
        <p className="text-zinc-500 font-mono text-sm py-8 text-center">Loading systems...</p>
      ) : activeSystem ? (
        /* ── Drill-down: rules for a specific system ── */
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3 items-center">
            <button
              onClick={() => { setActiveSystem(null); fetchSystems(selectedLang); }}
              className="px-3 py-1.5 rounded text-sm font-medium bg-zinc-700 text-zinc-200 hover:bg-zinc-600"
            >
              &larr; Back
            </button>
            <div className="flex-1">
              <h3 className="text-zinc-100 font-medium text-sm">{activeSystem.name}</h3>
              <div className="flex gap-3 text-xs text-zinc-500 mt-0.5">
                <span className={`px-2 py-0.5 rounded ${SOURCE_BADGE[activeSystem.source] ?? 'bg-zinc-800 text-zinc-300'}`}>{activeSystem.source}</span>
                <span>{rules.length} rules</span>
                {!activeSystem.editable && <span className="text-amber-400">read-only</span>}
              </div>
            </div>
            {activeSystem.editable && !editing && (
              <div className="flex gap-2">
                <button
                  onClick={startEdit}
                  className="px-4 py-1.5 rounded text-sm font-medium bg-teal-800 text-teal-200 hover:bg-teal-700"
                >
                  {rules.length > 0 ? 'Edit Rules' : 'Add Rules'}
                </button>
                <button
                  onClick={() => deleteSystem(activeSystem)}
                  className="px-4 py-1.5 rounded text-sm font-medium bg-red-900/50 text-red-300 hover:bg-red-800/50"
                >
                  Delete
                </button>
              </div>
            )}
            {editing && (
              <div className="flex gap-2">
                <button
                  onClick={saveRules}
                  disabled={saving}
                  className="px-4 py-1.5 rounded text-sm font-medium bg-teal-800 text-teal-200 hover:bg-teal-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="px-4 py-1.5 rounded text-sm font-medium bg-zinc-700 text-zinc-200 hover:bg-zinc-600"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>

          {!editing && (
            <div className="flex flex-wrap gap-3 items-center">
              <input
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder="Filter rules..."
                className="flex-1 min-w-[150px] bg-zinc-800 text-zinc-200 rounded border border-zinc-700 px-3 py-1.5 text-sm focus:border-teal-600 focus:outline-none"
              />
              <span className="text-zinc-500 text-xs">
                {filtered.length} of {rules.length} rules
              </span>
            </div>
          )}

          {saveMsg && (
            <div className={`rounded px-3 py-2 text-xs ${saveMsg.startsWith('Saved') ? 'bg-emerald-900/30 border border-emerald-800/50 text-emerald-300' : 'bg-red-900/30 border border-red-800/50 text-red-300'}`}>
              {saveMsg}
            </div>
          )}

          {editing ? (
            <div className="space-y-2">
              <p className="text-zinc-500 text-xs">
                One rule per line. Format: <code className="text-zinc-300">"pattern","replacement"</code>
                — patterns are regex, applied in order top to bottom.
              </p>
              <textarea
                value={editText}
                onChange={e => setEditText(e.target.value)}
                className="w-full bg-zinc-900 text-zinc-200 rounded border border-zinc-700 px-3 py-2 text-sm font-mono min-h-[400px] resize-y focus:border-teal-600 focus:outline-none"
                spellCheck={false}
              />
            </div>
          ) : loadingRules ? (
            <p className="text-zinc-500 font-mono text-sm py-8 text-center">Loading rules...</p>
          ) : rules.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <p className="text-zinc-500 text-sm">No rules yet.</p>
              {activeSystem.editable && (
                <button
                  onClick={startEdit}
                  className="px-4 py-1.5 rounded text-sm font-medium bg-teal-800 text-teal-200 hover:bg-teal-700"
                >
                  Add Rules
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto bg-zinc-800 border border-zinc-700 rounded-lg">
              <table className="w-full text-sm border-collapse min-w-[500px]">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500 w-12">#</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Pattern (regex)</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-zinc-500">Replacement</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 500).map((r, i) => (
                    <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-700/20">
                      <td className="px-3 py-1.5 text-zinc-600 text-xs font-mono">{i + 1}</td>
                      <td className="px-3 py-1.5 font-mono text-xs text-zinc-200">{r.pattern}</td>
                      <td className="px-3 py-1.5 font-mono text-xs text-teal-300">{r.replacement || <span className="text-zinc-600">(empty)</span>}</td>
                    </tr>
                  ))}
                  {filtered.length > 500 && (
                    <tr>
                      <td colSpan={3} className="px-3 py-2 text-center text-zinc-500 text-xs">
                        Showing first 500 of {filtered.length} rules. Use filter to narrow.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* ── Systems list ── */
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <h3 className="text-zinc-300 text-sm font-medium">
              Transcription systems for <span className="text-teal-400 font-mono">{selectedLang}</span>
              {selectedMeta && <span className="text-zinc-500 font-normal ml-2">({selectedMeta.name})</span>}
            </h3>
            <button
              onClick={() => { setShowNewForm(true); setNewName(''); setSaveMsg(null); }}
              className="px-4 py-1.5 rounded text-sm font-medium bg-teal-800 text-teal-200 hover:bg-teal-700"
            >
              + Add Transcription System
            </button>
          </div>

          {showNewForm && (
            <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 space-y-3">
              <label className="text-zinc-400 text-xs block">
                System name (e.g. "Phonemic IPA", "Cyrillicization", "Arabization for Arabic UI")
              </label>
              <div className="flex flex-wrap gap-3">
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="Enter transcription system name..."
                  className="flex-1 min-w-[200px] bg-zinc-900 text-zinc-200 rounded border border-zinc-700 px-3 py-1.5 text-sm focus:border-teal-600 focus:outline-none"
                  onKeyDown={e => { if (e.key === 'Enter') createSystem(); }}
                  autoFocus
                />
                <button
                  onClick={createSystem}
                  disabled={saving || !newName.trim()}
                  className="px-4 py-1.5 rounded text-sm font-medium bg-teal-800 text-teal-200 hover:bg-teal-700 disabled:opacity-50"
                >
                  {saving ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={() => setShowNewForm(false)}
                  className="px-4 py-1.5 rounded text-sm font-medium bg-zinc-700 text-zinc-200 hover:bg-zinc-600"
                >
                  Cancel
                </button>
              </div>
              {saveMsg && !saveMsg.startsWith('Saved') && (
                <div className="bg-red-900/30 border border-red-800/50 rounded px-3 py-2 text-red-300 text-xs">{saveMsg}</div>
              )}
            </div>
          )}

          {systems.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <p className="text-zinc-500 text-sm">No transcription systems for <span className="text-zinc-300 font-mono">{selectedLang}</span> yet.</p>
              <p className="text-zinc-600 text-xs">Add a romanization, IPA transcription, cyrillicization, or any other system.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {systems.map(sys => (
                <button
                  key={sys.id}
                  onClick={() => openSystem(sys)}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 text-left hover:border-teal-600/50 hover:bg-zinc-800/80 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-zinc-100 font-medium text-sm truncate">{sys.name}</h4>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${SOURCE_BADGE[sys.source] ?? 'bg-zinc-800 text-zinc-300'}`}>
                          {sys.source}
                        </span>
                        <span className="text-zinc-500 text-xs">{sys.rule_count} rules</span>
                        {!sys.editable && <span className="text-amber-400 text-xs">read-only</span>}
                      </div>
                    </div>
                    <span className="text-zinc-600 text-sm">&rarr;</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────

interface WikiLuaInfo {
  code: string;
  name: string;
  wikt_code: string;
  types: string[];
  route: string;
  status: string;
  script_level?: boolean;
}

interface WikiLuaTotal {
  ipa: number;
  romanization: number;
  archive_files: number;
}

interface ApiResponse {
  languages: LanguageInfo[];
  claude_api: LanguageInfo[];
  planned: LanguageInfo[];
  wiki_lua?: WikiLuaInfo[];
  wiki_lua_scripts?: WikiLuaInfo[];
  wiki_lua_total?: WikiLuaTotal;
}

export default function Metagraph() {
  const [tab, setTab] = useState<Tab>('overview');
  const [languages, setLanguages] = useState<LanguageInfo[]>([]);
  const [claudeApi, setClaudeApi] = useState<LanguageInfo[]>([]);
  const [planned, setPlanned] = useState<LanguageInfo[]>([]);
  const [wikiLua, setWikiLua] = useState<WikiLuaInfo[]>([]);
  const [wikiLuaScripts, setWikiLuaScripts] = useState<WikiLuaInfo[]>([]);
  const [wikiLuaTotal, setWikiLuaTotal] = useState<WikiLuaTotal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/metagraph`)
      .then(r => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json() as Promise<ApiResponse>;
      })
      .then(d => {
        const byCode = (a: LanguageInfo, b: LanguageInfo) => a.code.localeCompare(b.code);
        setLanguages((d.languages ?? []).sort(byCode));
        setClaudeApi((d.claude_api ?? []).sort(byCode));
        setPlanned((d.planned ?? []).sort(byCode));
        setWikiLua((d.wiki_lua ?? []) as WikiLuaInfo[]);
        setWikiLuaScripts((d.wiki_lua_scripts ?? []) as WikiLuaInfo[]);
        setWikiLuaTotal(d.wiki_lua_total ?? null);
        setLoading(false);
      })
      .catch(err => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        setLoading(false);
      });
  }, []);

  const allLanguages = [...languages, ...claudeApi, ...planned];

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-zinc-100">Metagraph Transcriptions</h1>
        <p className="text-zinc-500 font-mono text-sm py-8 text-center">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-zinc-100">Metagraph Transcriptions</h1>
        <p className="text-red-400 font-mono text-sm py-8 text-center">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">Metagraph Transcriptions</h1>
          <p className="text-zinc-500 text-xs mt-1">{languages.length} Metagraph + {claudeApi.length} Claude API + {planned.length} planned + {wikiLua.length} Wiki Lua — {languages.reduce((s, l) => s + (l.rule_count ?? 0), 0).toLocaleString()} rules</p>
        </div>
        <div className="flex gap-1 bg-zinc-800 rounded p-0.5">
          {(['overview', 'test', 'rules'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${
                tab === t ? 'bg-teal-800 text-teal-200' : 'text-zinc-400 hover:text-zinc-200'
              }`}
            >
              {t === 'overview' ? 'Overview' : t === 'test' ? 'Test' : 'Rules'}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' ? <Overview languages={languages} claudeApi={claudeApi} planned={planned} wikiLua={wikiLua} wikiLuaScripts={wikiLuaScripts} wikiLuaTotal={wikiLuaTotal} /> :
       tab === 'test' ? <TestPanel languages={languages} claudeApi={claudeApi} /> :
       <RulesViewer allLanguages={allLanguages} />}
    </div>
  );
}
